import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { ArrowLeft, FileText, Languages, History, Trash2, Copy } from 'lucide-react';
import { formatCurrency } from '../utils/currency';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LABELS = {
  id: {
    title: 'REKAPITULASI PROGRESS PEKERJAAN',
    no: 'No.', project: 'Project', subWork: 'List Project / Bangunan',
    cost: 'Biaya Pekerjaan', status: 'Status', progress: 'Progress',
    paidAmount: 'Fee Pekerjaan Sudah Dibayarkan',
    notes: 'Catatan',
    totalCost: 'Total Biaya Pekerjaan',
    totalPaidLabel: 'Total Sudah Dibayarkan',
    weightedProgress: 'Progress Tertimbang',
  },
  en: {
    title: 'JOB PROGRESS RECAP',
    no: 'No.', project: 'Project', subWork: 'Building / Sub-Work',
    cost: 'Cost of Work', status: 'Status', progress: 'Progress',
    paidAmount: 'Work Fee Already Paid',
    notes: 'Notes',
    totalCost: 'Total Cost of Work',
    totalPaidLabel: 'Total Already Paid',
    weightedProgress: 'Weighted Progress',
  }
};

const STATUS_OPTIONS = ['On Progress', 'Selesai', 'Belum Dimulai'];

const ClientReport = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(searchParams.get('clientId') || '');
  const [selectedProjectIds, setSelectedProjectIds] = useState([]);
  const [lang, setLang] = useState('id');
  const [reportData, setReportData] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [pastReports, setPastReports] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => { fetchData(); }, []);
  useEffect(() => {
    if (selectedClientId) {
      axios.get(`${API}/client-reports/${selectedClientId}`).then(res => setPastReports(res.data)).catch(() => {});
    }
  }, [selectedClientId]);

  const fetchData = async () => {
    try {
      const [c, p] = await Promise.all([axios.get(`${API}/clients`), axios.get(`${API}/projects`)]);
      setClients(c.data);
      setProjects(p.data);
    } catch { toast.error('Failed to load data'); }
  };

  const clientProjects = projects.filter(p => p.clientId === selectedClientId);
  const toggleProject = (pid) => setSelectedProjectIds(prev => prev.includes(pid) ? prev.filter(id => id !== pid) : [...prev, pid]);

  const calcWeightedProgress = (subWorks) => {
    const total = subWorks.reduce((s, sw) => s + (sw.hiddenPrice || 0), 0);
    if (total === 0) return 0;
    return Math.round(subWorks.reduce((s, sw) => s + (sw.hiddenPrice || 0) * (sw.progress || 0), 0) / total * 100) / 100;
  };

  const generatePreview = () => {
    if (selectedProjectIds.length === 0) { toast.error('Please select at least one project'); return; }
    const data = selectedProjectIds.map((pid, idx) => {
      const project = projects.find(p => p.id === pid);
      if (!project) return null;
      const sws = (project.subWorks?.length > 0)
        ? project.subWorks.map(sw => ({ name: sw.name, hiddenPrice: sw.price || 0, status: 'On Progress', progress: 0, notes: '' }))
        : [{ name: project.name, hiddenPrice: project.contractValue, status: 'On Progress', progress: 0, notes: '' }];

      // Snapshot the project's actual payment state at report generation time.
      // paidAmount comes from project.amountPaid (always up-to-date via transactions).
      // paidPercent is shown alongside in brackets, e.g. "Rp 200.000.000 (61%)".
      const paidAmount = project.amountPaid || 0;
      const cv = project.contractValue || 0;
      const paidPercent = cv > 0
        ? Math.round((paidAmount / cv) * 100 * 100) / 100
        : 0;

      return {
        no: idx + 1,
        projectId: pid,
        projectName: project.name,
        contractValue: cv,
        subWorks: sws,
        paidAmount,
        paidPercent,
        notes: '',
      };
    }).filter(Boolean);
    setReportData(data);
    setShowPreview(true);
  };

  const updateSW = (pi, si, field, val) => {
    const d = [...reportData];
    d[pi].subWorks[si][field] = field === 'progress' ? (parseFloat(val) || 0) : val;
    setReportData(d);
  };

  const updateProj = (pi, field, val) => {
    const d = [...reportData];
    d[pi][field] = val;
    setReportData(d);
  };

  // Pre-fill notes (both project-level and sub-work level) from the most recent
  // saved report for this client. Matches by projectName and sub-work name.
  const copyNotesFromLastReport = () => {
    if (!pastReports || pastReports.length === 0) {
      toast.error('No past reports available for this client');
      return;
    }
    const last = pastReports[0]; // backend returns sorted by createdAt desc
    const d = reportData.map(p => {
      const lastProj = (last.reportData || []).find(lp => lp.projectName === p.projectName);
      if (!lastProj) return p;
      return {
        ...p,
        notes: lastProj.notes || p.notes || '',
        subWorks: p.subWorks.map(sw => {
          const lastSw = (lastProj.subWorks || []).find(s => s.name === sw.name);
          return lastSw ? { ...sw, notes: lastSw.notes || sw.notes || '' } : sw;
        }),
      };
    });
    setReportData(d);
    toast.success('Notes loaded from last report');
  };

  const L = LABELS[lang];
  const fmtRp = (v) => `Rp ${(v || 0).toLocaleString('id-ID')}`;

  const generatePDF = async () => {
    const doc = new jsPDF({ orientation: 'landscape' });
    const pw = doc.internal.pageSize.getWidth();
    let y = 15;
    doc.setFontSize(13); doc.setFont('helvetica', 'bold');
    doc.text(L.title, pw / 2, y, { align: 'center' }); y += 10;
    const client = clients.find(c => c.id === selectedClientId);
    if (client) { doc.setFontSize(10); doc.setFont('helvetica', 'normal'); doc.text(`Client: ${client.name} - ${client.company}`, 14, y); y += 7; }

    const head = [[L.no, L.project, L.subWork, L.cost, L.status, L.progress, L.paidAmount, L.notes]];
    const body = [];
    let gCost = 0, gPaid = 0;

    reportData.forEach(proj => {
      const wp = calcWeightedProgress(proj.subWorks);
      gCost += proj.contractValue;
      gPaid += proj.paidAmount || 0;
      const paidCell = (proj.paidAmount || 0) > 0
        ? `${fmtRp(proj.paidAmount)} (${Math.round(proj.paidPercent)}%)`
        : `${fmtRp(0)} (0%)`;
      // Project row
      body.push([
        String(proj.no),
        { content: proj.projectName, styles: { fontStyle: 'bold' } },
        '',
        fmtRp(proj.contractValue),
        '',
        { content: `${wp}%`, styles: { fontStyle: 'bold' } },
        paidCell,
        proj.notes || ''
      ]);
      // Sub-work rows (notes column shows the per-sub-work note)
      proj.subWorks.forEach(sw => {
        body.push(['', '', `  - ${sw.name}`, '', sw.status, `${sw.progress}%`, '', sw.notes || '']);
      });
    });

    body.push([{ content: L.totalCost, colSpan: 3, styles: { fontStyle: 'bold' } }, { content: fmtRp(gCost), styles: { fontStyle: 'bold' } }, '', '', '', '']);
    body.push([{ content: L.totalPaidLabel, colSpan: 6, styles: { fontStyle: 'bold', halign: 'right' } }, { content: fmtRp(gPaid), styles: { fontStyle: 'bold' } }, '']);

    autoTable(doc, {
      startY: y, head, body, theme: 'grid',
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [40, 40, 40], textColor: [255, 255, 255], fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 10 },
        3: { halign: 'right', cellWidth: 28 },
        5: { halign: 'center', cellWidth: 18 },
        6: { halign: 'right', cellWidth: 50 },
        7: { cellWidth: 60 },
      },
      margin: { left: 10, right: 10 },
    });

    const now = new Date();
    const ds = `${now.getDate().toString().padStart(2,'0')}-${(now.getMonth()+1).toString().padStart(2,'0')}-${now.getFullYear()}`;
    doc.save(`Client_Report_${client?.name || 'Report'}_${ds}.pdf`);

    try {
      await axios.post(`${API}/client-reports`, {
        clientId: selectedClientId, clientName: client ? `${client.name} - ${client.company}` : '',
        reportData: reportData.map(p => ({
          projectName: p.projectName,
          contractValue: p.contractValue,
          paidAmount: p.paidAmount || 0,
          paidPercent: p.paidPercent || 0,
          notes: p.notes || '',
          proratedProgress: calcWeightedProgress(p.subWorks),
          subWorks: p.subWorks.map(sw => ({ name: sw.name, status: sw.status, progress: sw.progress, notes: sw.notes || '' }))
        })),
        language: lang
      });
      toast.success('Report saved & PDF generated!');
      const res = await axios.get(`${API}/client-reports/${selectedClientId}`);
      setPastReports(res.data);
    } catch { toast.success('PDF generated!'); }
  };

  return (
    <div className="min-h-screen p-6" data-testid="client-report-page">
      <div className="max-w-7xl mx-auto">
        <Button variant="ghost" onClick={() => navigate('/clients')} className="mb-4" data-testid="back-to-clients">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Clients
        </Button>
        <div className="flex items-center justify-between mb-6 relative">
          <span className="text-[10px] font-mono text-slate-400 bg-slate-100 px-2 py-0.5 rounded absolute top-0 right-0">A.4.1</span>
          <div>
            <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Work Sans, sans-serif' }}>Client Report</h1>
            <p className="text-slate-600">Generate billing attachment for your client</p>
          </div>
          <div className="flex gap-2">
            {pastReports.length > 0 && (
              <Button variant="outline" size="sm" onClick={() => setShowHistory(!showHistory)} data-testid="toggle-history">
                <History className="w-4 h-4 mr-2" /> Past Reports ({pastReports.length})
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => setLang(lang === 'id' ? 'en' : 'id')} data-testid="lang-switch">
              <Languages className="w-4 h-4 mr-2" /> {lang === 'id' ? 'English' : 'Indonesia'}
            </Button>
          </div>
        </div>

        {showHistory && pastReports.length > 0 && (
          <Card className="mb-6 border-amber-200 bg-amber-50">
            <CardHeader><CardTitle className="text-amber-900 text-base">Past Reports</CardTitle></CardHeader>
            <CardContent>
              {pastReports.map((r, i) => (
                <div key={r.id} className="p-3 bg-white rounded border text-sm mb-2">
                  <div className="flex justify-between items-center mb-1">
                    <span className="font-medium">Report #{pastReports.length - i}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500">{new Date(r.createdAt).toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
                      <button
                        onClick={async () => {
                          try {
                            await axios.delete(`${API}/client-reports/${r.id}`);
                            setPastReports(prev => prev.filter(p => p.id !== r.id));
                            toast.success('Report deleted');
                          } catch { toast.error('Failed to delete'); }
                        }}
                        className="text-red-400 hover:text-red-600 p-1"
                        title="Delete this report"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  {r.reportData?.map((p, j) => {
                    const hasPaid = p.paidAmount !== undefined && p.paidAmount !== null;
                    return (
                      <div key={j} className="ml-4 mb-2 last:mb-0">
                        <div className="flex justify-between text-xs text-slate-700 font-medium">
                          <span>{p.projectName}</span>
                          <span className="text-slate-600">
                            Progress: {p.proratedProgress}%
                            {hasPaid
                              ? ` | Paid: ${formatCurrency(p.paidAmount)} (${Math.round(p.paidPercent || 0)}%)`
                              : (p.billingPercent !== undefined ? ` | Billed: ${p.billingPercent}% (${formatCurrency(p.totalBilled || 0)})` : '')
                            }
                          </span>
                        </div>
                        {p.notes && (
                          <div className="ml-2 mt-1 text-[11px] text-slate-600 italic whitespace-pre-wrap">
                            <span className="font-semibold not-italic">Catatan: </span>{p.notes}
                          </div>
                        )}
                        {p.subWorks && p.subWorks.length > 0 && (
                          <div className="ml-4 mt-1 space-y-0.5">
                            {p.subWorks.map((sw, k) => (
                              <div key={k} className="text-[11px] text-slate-500">
                                - {sw.name}: <span className="text-slate-700">{sw.progress}%</span>
                                {sw.status ? ` (${sw.status})` : ''}
                                {sw.notes ? <span className="block ml-3 italic text-slate-600">↳ {sw.notes}</span> : null}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        <Card className="mb-6">
          <CardHeader><CardTitle>1. Select Client</CardTitle></CardHeader>
          <CardContent>
            <Select value={selectedClientId} onValueChange={(v) => { setSelectedClientId(v); setSelectedProjectIds([]); setShowPreview(false); }}>
              <SelectTrigger data-testid="report-client-select"><SelectValue placeholder="Choose a client" /></SelectTrigger>
              <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name} - {c.company}</SelectItem>)}</SelectContent>
            </Select>
          </CardContent>
        </Card>

        {selectedClientId && (
          <Card className="mb-6">
            <CardHeader><CardTitle>2. Select Projects</CardTitle></CardHeader>
            <CardContent>
              {clientProjects.length === 0 ? (
                <p className="text-slate-500 text-sm">No projects for this client. Assign client to projects first.</p>
              ) : (
                <div className="space-y-2">
                  {clientProjects.map(p => (
                    <div key={p.id} className="flex items-center gap-3 p-2 rounded hover:bg-slate-50">
                      <Checkbox checked={selectedProjectIds.includes(p.id)} onCheckedChange={() => toggleProject(p.id)} />
                      <span className="font-medium">{p.name}</span>
                      <span className="text-sm text-slate-500">{formatCurrency(p.contractValue)}</span>
                    </div>
                  ))}
                  <Button onClick={generatePreview} className="mt-4" data-testid="generate-preview-btn">Generate Preview</Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {showPreview && reportData.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>3. Edit & Generate</CardTitle>
                  <p className="text-xs text-slate-500 mt-1">Fill in progress % per sub-work. Weighted progress auto-calculates next to project name.</p>
                </div>
                <div className="flex items-center gap-2">
                  {pastReports.length > 0 && (
                    <Button
                      variant="outline"
                      onClick={copyNotesFromLastReport}
                      data-testid="copy-notes-btn"
                      title="Pre-fill notes (project + sub-work) from the most recent saved report"
                    >
                      <Copy className="w-4 h-4 mr-2" /> Copy Notes from Last Report
                    </Button>
                  )}
                  <Button onClick={generatePDF} className="bg-slate-800 hover:bg-slate-900" data-testid="generate-pdf-btn">
                    <FileText className="w-4 h-4 mr-2" /> Save & Generate PDF
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">{L.no}</TableHead>
                    <TableHead>{L.project}</TableHead>
                    <TableHead>{L.subWork}</TableHead>
                    <TableHead className="text-right">{L.cost}</TableHead>
                    <TableHead>{L.status}</TableHead>
                    <TableHead className="text-center">{L.progress}</TableHead>
                    <TableHead className="text-right">{L.paidAmount}</TableHead>
                    <TableHead className="min-w-[180px]">{L.notes}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reportData.map((proj, pi) => (
                    <React.Fragment key={pi}>
                      {/* Project Header Row */}
                      <TableRow className="bg-slate-50 border-t-2">
                        <TableCell className="font-bold">{proj.no}</TableCell>
                        <TableCell className="font-bold">{proj.projectName}</TableCell>
                        <TableCell></TableCell>
                        <TableCell className="text-right font-bold">{formatCurrency(proj.contractValue)}</TableCell>
                        <TableCell></TableCell>
                        <TableCell className="text-center font-bold text-blue-700">{calcWeightedProgress(proj.subWorks)}%</TableCell>
                        <TableCell className="text-right font-medium">
                          <div className="text-sm">{formatCurrency(proj.paidAmount || 0)}</div>
                          <div className={`text-xs ${proj.paidPercent >= 100 ? 'text-green-600 font-bold' : proj.paidPercent > 0 ? 'text-amber-700' : 'text-slate-400'}`}>
                            ({Math.round(proj.paidPercent || 0)}%)
                          </div>
                        </TableCell>
                        <TableCell>
                          <Textarea
                            value={proj.notes || ''}
                            onChange={(e) => updateProj(pi, 'notes', e.target.value)}
                            placeholder="e.g. Sub-work XYZ belum dimulai karena menunggu approval..."
                            className="min-h-[60px] text-xs"
                            data-testid={`project-notes-${pi}`}
                          />
                        </TableCell>
                      </TableRow>
                      {/* Sub-work rows */}
                      {proj.subWorks.map((sw, si) => (
                        <TableRow key={`${pi}-${si}`} className="border-t-0">
                          <TableCell></TableCell>
                          <TableCell></TableCell>
                          <TableCell className="text-sm text-slate-600 pl-6">- {sw.name}</TableCell>
                          <TableCell></TableCell>
                          <TableCell>
                            <Select value={sw.status} onValueChange={(v) => updateSW(pi, si, 'status', v)}>
                              <SelectTrigger className="h-7 text-xs w-32"><SelectValue /></SelectTrigger>
                              <SelectContent>{STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Input type="number" value={sw.progress} onChange={(e) => updateSW(pi, si, 'progress', e.target.value)} className="h-7 w-16 text-xs text-center" min="0" max="100" />
                          </TableCell>
                          <TableCell></TableCell>
                          <TableCell>
                            <Textarea
                              value={sw.notes || ''}
                              onChange={(e) => updateSW(pi, si, 'notes', e.target.value)}
                              placeholder="Sub-work note..."
                              className="min-h-[44px] text-xs"
                              data-testid={`subwork-notes-${pi}-${si}`}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  ))}
                  <TableRow className="bg-slate-100 font-bold border-t-2">
                    <TableCell colSpan={3} className="text-right">{L.totalCost}</TableCell>
                    <TableCell className="text-right">{formatCurrency(reportData.reduce((s, p) => s + p.contractValue, 0))}</TableCell>
                    <TableCell colSpan={2}></TableCell>
                    <TableCell className="text-right">{formatCurrency(reportData.reduce((s, p) => s + (p.paidAmount || 0), 0))}</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                  <TableRow className="bg-slate-100 font-bold">
                    <TableCell colSpan={6} className="text-right text-xs text-slate-600">{L.totalPaidLabel}</TableCell>
                    <TableCell className="text-right">{formatCurrency(reportData.reduce((s, p) => s + (p.paidAmount || 0), 0))}</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ClientReport;
